import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HistoryImportRun, HistoryImportRunStatus } from '@troia-v3/types';

import { historyImportApi } from '../services/historyImportApi';

/**
 * Status terminais — polling do run pode parar quando alcançado. Exportado
 * para o modal (Task 18) decidir se um `runId` ativo precisa de confirmação
 * + abort antes de fechar.
 */
export const HISTORY_IMPORT_TERMINAL_STATUSES: ReadonlySet<HistoryImportRunStatus> = new Set([
  'completed',
  'failed',
  'aborted',
  'expired',
]);

export const historyImportKeys = {
  all: ['history-imports'] as const,
  run: (runId: string) => [...historyImportKeys.all, 'run', runId] as const,
  chats: (runId: string, page: number, search?: string) =>
    [...historyImportKeys.all, 'chats', runId, page, search ?? ''] as const,
};

/**
 * Inicia uma sessão de importação de histórico (Passo 1 do wizard — Task 18).
 *
 * @example
 * const startSession = useStartImportSession();
 * const run = await startSession.mutateAsync({ channelId, periodDays: 30 });
 */
export const useStartImportSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channelId, periodDays }: { channelId: string; periodDays: number }) =>
      historyImportApi.startSession(channelId, periodDays),
    onSuccess: (run) => {
      queryClient.setQueryData(historyImportKeys.run(run.id), run);
    },
  });
};

/**
 * Poll do estado de uma run de importação. Refetch automático a cada 5s
 * enquanto o status não for terminal (completed/failed/aborted/expired).
 *
 * @example
 * const { data: run } = useImportRun(runId);
 */
export const useImportRun = (runId: string | undefined) => {
  return useQuery<HistoryImportRun>({
    queryKey: historyImportKeys.run(runId ?? ''),
    queryFn: () => historyImportApi.getRun(runId as string),
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && HISTORY_IMPORT_TERMINAL_STATUSES.has(status)) return false;
      return 5000;
    },
  });
};

/**
 * Lista paginada dos chats capturados por uma run. Só busca quando a run
 * está em `ready_for_selection` (chats já capturados e prontos pra seleção).
 *
 * @example
 * const { data } = useImportChats(runId, run?.status, page, search);
 */
export const useImportChats = (
  runId: string | undefined,
  runStatus: HistoryImportRunStatus | undefined,
  page: number,
  search?: string,
) => {
  return useQuery({
    queryKey: historyImportKeys.chats(runId ?? '', page, search),
    queryFn: () => historyImportApi.listChats(runId as string, page, search),
    enabled: !!runId && runStatus === 'ready_for_selection',
  });
};

/**
 * Dispara a ingestão dos chats selecionados (Passo final do wizard — Task 18).
 */
export const useIngestImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      chatIds,
      periodStart,
      periodEnd,
    }: {
      runId: string;
      chatIds: string[];
      periodStart: string;
      periodEnd: string;
    }) => historyImportApi.ingest(runId, chatIds, periodStart, periodEnd),
    onSuccess: (_data, { runId }) => {
      void queryClient.invalidateQueries({ queryKey: historyImportKeys.run(runId) });
    },
  });
};

/**
 * Aborta uma run de importação em andamento.
 */
export const useAbortImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => historyImportApi.abort(runId),
    onSuccess: (_data, runId) => {
      void queryClient.invalidateQueries({ queryKey: historyImportKeys.run(runId) });
    },
  });
};
